import { useCallback, useEffect, useState } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  CodeSnippet,
  Content,
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';

interface TokenMetadata {
  tokenIdSuffix: string;
  createdAt: string;
  lastUsedAt?: string;
}

interface TokenStatus {
  exists: boolean;
  token: TokenMetadata | null;
  /**
   * Idle window the backend enforces, in days. Optional so a response without it says nothing
   * about expiry rather than claiming a token is revoked after zero days.
   */
  idleRevokeDays?: number;
}

function formatTimestamp(value?: string): string {
  if (!value) {
    return 'never';
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

// Single-quoted so the user's shell passes ${MCP_TOKEN} through and Claude Code expands it at
// runtime, which keeps the secret out of ~/.claude.json.
function claudeSetupCommand(mcpUrl: string): string {
  return `claude mcp add --transport http lexipol_backstage \\
  ${mcpUrl} \\
  --header 'Authorization: Bearer \${MCP_TOKEN}' \\
  --scope user`;
}

/**
 * Personal MCP token management for the signed-in user.
 *
 * `fetchApiRef` attaches the user's Backstage token, which is what makes the user-only routes
 * reachable. The backend derives the owner from those credentials, so there is no way to reach
 * anyone else's token from here.
 */
export const McpTokenCard = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [status, setStatus] = useState<TokenStatus | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Component state only, gone on navigate — the backend cannot return it a second time.
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [mcpUrl, setMcpUrl] = useState<string | null>(null);

  // Resolved through discovery rather than hardcoded: tokens only work against the deployment
  // that minted them, so the command must name this deployment's endpoint.
  useEffect(() => {
    discoveryApi.getBaseUrl('mcp-actions').then(
      base => setMcpUrl(`${base}/v1`),
      () => {},
    );
  }, [discoveryApi]);

  const request = useCallback(
    async (method: 'GET' | 'POST' | 'DELETE') => {
      const baseUrl = await discoveryApi.getBaseUrl('mcp-tokens');
      const response = await fetchApi.fetch(`${baseUrl}/token`, { method });
      if (!response.ok) {
        throw new Error(`Request failed: ${response.status} ${response.statusText}`);
      }
      return response.json();
    },
    [discoveryApi, fetchApi],
  );

  const refresh = useCallback(async () => {
    const next = await request('GET');
    setStatus(next);
    setError(null);
  }, [request]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const next = await request('GET');
        if (!cancelled) {
          setStatus(next);
        }
      } catch (e) {
        if (!cancelled) {
          // Leave status null. The render must not treat "could not load" as "no token" —
          // offering Create there would replace an existing token with no confirmation.
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [request]);

  const reload = async () => {
    setBusy(true);
    try {
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const created = await request('POST');
      setPlaintext(created.token);
      // Carry the idle window over: only the GET reports it, so building this state from the POST
      // alone drops the expiry note at the one moment it matters most — right after minting, with
      // the secret on screen about to be pasted into a client config.
      setStatus(prev => ({
        exists: true,
        token: created.metadata,
        idleRevokeDays: prev?.idleRevokeDays,
      }));
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
      setConfirmReplace(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    setError(null);
    try {
      await request('DELETE');
      setPlaintext(null);
      setCopied(false);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!plaintext) {
      return;
    }
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
    } catch {
      // Clipboard access can be refused. The token is on screen and selectable, so this is fine.
      setCopied(false);
    }
  };

  /**
   * Three states, not two. "Could not load" must never render as "no token": offering Create there
   * replaced an existing token with no confirmation.
   */
  const renderActions = () => {
    if (status === null) {
      return (
        <Box>
          <Typography variant="body2" paragraph>
            Could not load your token status, so no actions are offered — creating one here could
            replace a token you already have without warning.
          </Typography>
          <Button variant="outlined" disabled={busy} onClick={reload}>
            Retry
          </Button>
        </Box>
      );
    }

    if (status.exists && status.token) {
      return (
        <>
          <Typography variant="body2">
            <strong>Token ending:</strong> <code>…{status.token.tokenIdSuffix}</code>
          </Typography>
          <Typography variant="body2">
            <strong>Created:</strong> {formatTimestamp(status.token.createdAt)}
          </Typography>
          <Typography variant="body2">
            <strong>Last used:</strong> {formatTimestamp(status.token.lastUsedAt)}
          </Typography>
          {/* Sits under "Last used" because that is the field the sweep reads. */}
          {Boolean(status.idleRevokeDays && status.idleRevokeDays > 0) && (
            <Typography variant="body2" paragraph color="textSecondary">
              Go {status.idleRevokeDays} days without using this token and it is revoked
              automatically. Any use resets that; if it does lapse, create a new one here.
            </Typography>
          )}
          <Box display="flex" gridGap={8}>
            <Button
              variant="contained"
              color="primary"
              disabled={busy}
              onClick={() => setConfirmReplace(true)}
            >
              Recreate
            </Button>
            <Button variant="outlined" disabled={busy} onClick={revoke}>
              Revoke
            </Button>
          </Box>
        </>
      );
    }

    return (
      <Box>
        <Typography variant="body2" paragraph>
          You do not have a token yet.
        </Typography>
        <Button variant="contained" color="primary" disabled={busy} onClick={create}>
          Create token
        </Button>
      </Box>
    );
  };

  if (loading) {
    return <Progress />;
  }

  return (
    <Content>
      <Grid container spacing={3} direction="column">
        <Grid item>
          <InfoCard title="MCP access token">
            <Typography variant="body1" paragraph>
              A personal token lets an MCP client, such as Claude Code, call this Backstage instance
              on your behalf. Every tool call is recorded against your name, so keep it private and
              revoke it if it leaks.
            </Typography>
            <Typography variant="body2" paragraph color="textSecondary">
              You can have one token at a time. Creating a new one immediately stops the old one
              from working.
            </Typography>

            {error && (
              <Box mb={2}>
                <ResponseErrorPanel error={error} />
              </Box>
            )}

            {plaintext && (
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Copy your token now. This is the only time it is shown.
                </Typography>
                <TextField
                  value={plaintext}
                  variant="outlined"
                  fullWidth
                  size="small"
                  // Read-only, not disabled, so it stays selectable when the clipboard is refused.
                  InputProps={{ readOnly: true }}
                  inputProps={{ 'aria-label': 'MCP access token', spellCheck: false }}
                />
                <Box mt={1}>
                  <Button variant="outlined" onClick={copy}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </Box>
              </Box>
            )}

            {renderActions()}

            {mcpUrl && (
              <>
                <Divider style={{ margin: '16px 0' }} />
                <Typography variant="subtitle2" gutterBottom>
                  Set up Claude Code
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Export <code>MCP_TOKEN</code> in your shell profile first.
                </Typography>
                <CodeSnippet text={claudeSetupCommand(mcpUrl)} language="bash" showCopyCodeButton />
              </>
            )}
          </InfoCard>
        </Grid>
      </Grid>

      <Dialog open={confirmReplace} onClose={() => setConfirmReplace(false)}>
        <DialogTitle>Recreate your MCP token?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your current token stops working immediately. Any MCP client still configured with it
            will start failing until you update it with the new one.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReplace(false)}>Cancel</Button>
          <Button color="primary" disabled={busy} onClick={create}>
            Recreate
          </Button>
        </DialogActions>
      </Dialog>
    </Content>
  );
};
